/**
 * Lead List Component
 * ===================
 * Displays paginated list of leads with filtering and search
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { leadAPI } from '../../api';
import { Card, CardBody, Button, Badge, FilterDropdown, Alert } from '../ui';

const LeadList = ({ onLeadSelect, refreshTrigger }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    lead_source_type: '',
    search: '',
    sort_by: 'created_at',
    order: 'desc',
  });

  // Tracks the most recent request so out-of-order responses can't clobber state
  const requestIdRef = useRef(0);

  const fetchLeads = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await leadAPI.listLeads({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      // Ignore responses from superseded requests (e.g. rapid page changes)
      if (requestId !== requestIdRef.current) {
        return;
      }

      // Backend returns {data: {leads: [...], pagination: {...}}, meta: {...}}
      if (response.data) {
        setLeads(response.data.leads);
        setPagination((prev) => ({
          ...prev,
          ...response.data.pagination,
        }));
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads, refreshTrigger]);

  // Debounce search input → filters.search
  const prevSearch = useRef(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== prevSearch.current) {
        prevSearch.current = searchInput;
        setFilters((prev) => ({ ...prev, search: searchInput }));
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSourceTypeFilter = (lead_source_type) => {
    setFilters((prev) => ({ ...prev, lead_source_type }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { variant: 'info', label: 'New' },
      meeting_scheduled: { variant: 'accent', label: 'Meeting Scheduled' },
      contacted: { variant: 'warning', label: 'Contacted' },
      followup: { variant: 'warning', label: 'Follow Up' },
      questionnaire_completed: { variant: 'primary', label: 'Questionnaire Completed' },
      pre_proposal: { variant: 'primary', label: 'Pre-Proposal' },
      proposal_generation: { variant: 'success', label: 'Proposal Generation' },
      qualified: { variant: 'success', label: 'Qualified' },
      declined: { variant: 'danger', label: 'Declined' },
      converted: { variant: 'success', label: 'Converted' },
    };
    const config = statusConfig[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getSourceTypeBadge = (sourceType) => {
    const sourceConfig = {
      mock: { variant: 'warning', label: 'Mock' },
      crm: { variant: 'accent', label: 'CRM' },
      manual: { variant: 'primary', label: 'Manual' },
    };
    const config = sourceConfig[sourceType] || { variant: 'default', label: sourceType || 'Unknown' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTierBadge = (tier) => {
    const tierConfig = {
      tier_1: { variant: 'success', label: 'T1' },
      tier_2: { variant: 'primary', label: 'T2' },
      tier_3: { variant: 'accent', label: 'T3' },
      tier_4: { variant: 'warning', label: 'T4' },
      tier_5: { variant: 'default', label: 'T5' },
    };
    const config = tierConfig[tier] || tierConfig.tier_5;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };


  if (loading && leads.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading leads...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    const isNetwork = error.code === 'NETWORK_ERROR';
    return (
      <Card>
        <CardBody>
          <div className="py-8 px-4">
            <Alert
              variant={isNetwork ? 'info' : 'error'}
              title={isNetwork ? 'Connection Issue' : 'Failed to Load Leads'}
            >
              {error.message || 'An unexpected error occurred.'}
            </Alert>
            <div className="mt-4 text-center">
              <Button onClick={fetchLeads}>Retry</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        {/* Search & Filters */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          {/* Search */}
          <div className="relative sm:col-span-2">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, company, or email..."
              value={searchInput}
              onChange={handleSearch}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-[#3A3C3E] rounded-lg bg-white dark:bg-[#242628] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-primary"
            />
          </div>

          {/* Status Dropdown */}
          <FilterDropdown
            value={filters.status}
            onChange={handleStatusFilter}
            options={[
              { value: '', label: 'All Status' },
              { value: 'new', label: 'New' },
              { value: 'meeting_scheduled', label: 'Meeting Scheduled' },
              { value: 'contacted', label: 'Contacted' },
              { value: 'followup', label: 'Follow Up' },
              { value: 'questionnaire_completed', label: 'Questionnaire Completed' },
              { value: 'pre_proposal', label: 'Pre-Proposal' },
              { value: 'declined', label: 'Declined' },
            ]}
          />

          {/* Source Dropdown */}
          <FilterDropdown
            value={filters.lead_source_type}
            onChange={handleSourceTypeFilter}
            options={[
              { value: '', label: 'All Sources' },
              { value: 'manual', label: 'Manual' },
              { value: 'crm', label: 'CRM' },
              { value: 'mock', label: 'Mock' },
            ]}
          />
        </div>

        {/* Leads Table */}
        {leads.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.status || filters.lead_source_type
                ? 'Try adjusting your filters'
                : 'Get started by creating a new lead'}
            </p>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => onLeadSelect && onLeadSelect(lead.id)}
                  className="cursor-pointer rounded-lg border border-gray-200 dark:border-[#3A3C3E] bg-white dark:bg-[#242628] p-4 active:bg-gray-50 dark:active:bg-[#2E3033]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{lead.name}</div>
                      {lead.email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{lead.email}</div>
                      )}
                      {lead.company && (
                        <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 truncate">{lead.company}</div>
                      )}
                    </div>
                    {getTierBadge(lead.tier || 'tier_5')}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {getStatusBadge(lead.status)}
                    {getSourceTypeBadge(lead.lead_source_type)}
                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => onLeadSelect && onLeadSelect(lead.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                        <div className="text-sm text-gray-500">{lead.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.company}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSourceTypeBadge(lead.lead_source_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(lead.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTierBadge(lead.tier || 'tier_5')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="outline-primary"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLeadSelect && onLeadSelect(lead.id);
                          }}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    disabled={loading || pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={loading || pagination.page === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default LeadList;

